import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MapPin, Calendar, TrendingUp, CheckCircle, XCircle, ShieldAlert, RefreshCw, Settings as SettingsIcon, Save, Mail, MailOpen, Trash2, FileText, Bell, ShieldCheck, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LocationPreviewDialog from '../components/LocationPreviewDialog';
import { adminAPI } from '../utils/api';
import { toast } from 'sonner';

const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [overview, setOverview] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [previewLocation, setPreviewLocation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [interest, setInterest] = useState([]);
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('currentUser');
    if (!stored) {
      navigate('/login');
      return;
    }
    const parsed = JSON.parse(stored);
    if (!parsed.is_admin) {
      toast.error('Tato stránka je pouze pro administrátora');
      navigate('/dashboard');
      return;
    }
    setUser(parsed);
    fetchAll();
  }, [navigate]);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [ov, bk, us, lc, msgs, unread, intr, cons] = await Promise.all([
        adminAPI.getOverview(),
        adminAPI.listBookings({ limit: 50 }),
        adminAPI.listUsers(),
        adminAPI.listLocations(),
        adminAPI.listContactMessages(),
        adminAPI.unreadContactCount(),
        adminAPI.listInterest(),
        adminAPI.listTermsConsents(),
      ]);
      setOverview(ov.data);
      setBookings(bk.data);
      setUsers(us.data);
      setLocations(lc.data);
      setMessages(msgs.data);
      setUnreadCount(unread.data.unread || 0);
      setInterest(intr.data || []);
      setConsents(cons.data || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Chyba při načítání admin dat');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id, read) => {
    try {
      await adminAPI.markContactRead(id, read);
      setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, is_read: read } : m)));
      setUnreadCount((c) => Math.max(0, c + (read ? -1 : 1)));
    } catch (e) {
      toast.error('Chyba');
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Smazat tuto zprávu?')) return;
    try {
      await adminAPI.deleteContactMessage(id);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success('Zpráva smazána');
    } catch (e) {
      toast.error('Chyba při mazání');
    }
  };

  const handleDeleteInterest = async (id) => {
    if (!window.confirm('Smazat tento zájem o rezervaci?')) return;
    try {
      await adminAPI.deleteInterest(id);
      setInterest((prev) => prev.filter((m) => m.id !== id));
      toast.success('Záznam smazán');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při mazání');
    }
  };

  const handleApprove = async (id) => {
    try {
      await adminAPI.approveLocation(id);
      toast.success('Místo schváleno');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    }
  };

  const handleSuspend = async (id) => {
    try {
      await adminAPI.suspendLocation(id);
      toast.success('Místo pozastaveno');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba');
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen">
      <div
        className="relative py-20"
      >
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-4 text-white">
          <Badge className="bg-red-600 mb-3">
            <ShieldAlert className="w-3 h-3 mr-1" />
            ADMIN
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-bold">Administrace platformy</h1>
          <p className="text-white/80 mt-2">Přehled WildCamping platformy</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">KPI přehled</h2>
          <Button onClick={fetchAll} variant="outline" disabled={loading} data-testid="admin-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Aktualizovat
          </Button>
        </div>

        {/* KPI grid */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Kpi label="Uživatelé" value={overview.users_count} icon={<Users className="w-6 h-6" />} />
            <Kpi
              label="Lokality"
              value={`${overview.active_locations} / ${overview.locations_count}`}
              icon={<MapPin className="w-6 h-6" />}
              hint="aktivních / celkem"
            />
            <Kpi
              label="Rezervace"
              value={overview.bookings_count}
              icon={<Calendar className="w-6 h-6" />}
              hint={`${overview.confirmed_bookings} potvrzených`}
            />
            <Kpi
              label="Provize 15%"
              value={`${overview.platform_fee_estimated.toLocaleString('cs-CZ')} Kč`}
              icon={<TrendingUp className="w-6 h-6 text-green-600" />}
              hint={`z hrubého ${overview.total_gross_revenue.toLocaleString('cs-CZ')} Kč`}
              highlight
            />
          </div>
        )}

        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="grid w-full max-w-5xl grid-cols-3 sm:grid-cols-10 bg-white/90 h-auto">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">
              Rezervace ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="locations" data-testid="admin-tab-locations">
              Lokality ({locations.length})
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="admin-tab-users">
              Uživatelé ({users.length})
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">
              Zprávy {unreadCount > 0 && (
                <Badge className="ml-1 bg-red-500 text-white">{unreadCount}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="interest" data-testid="admin-tab-interest">
              <Bell className="w-4 h-4 mr-1" />
              Zájemci ({interest.length})
            </TabsTrigger>
            <TabsTrigger value="consents" data-testid="admin-tab-consents">
              <ShieldCheck className="w-4 h-4 mr-1" />
              Souhlasy ({consents.length})
            </TabsTrigger>
            <TabsTrigger value="resend" data-testid="admin-tab-resend">
              <Mail className="w-4 h-4 mr-1" />
              E-maily
            </TabsTrigger>
            <TabsTrigger value="hiw" data-testid="admin-tab-hiw">
              Jak to funguje
            </TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">
              Kontakt
            </TabsTrigger>
            <TabsTrigger value="terms" data-testid="admin-tab-terms">
              <FileText className="w-4 h-4 mr-1" />
              Podmínky
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings">
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-left">
                      <Th>ID</Th>
                      <Th>Místo</Th>
                      <Th>Termín</Th>
                      <Th>Hosté</Th>
                      <Th>Cena</Th>
                      <Th>Status</Th>
                      <Th>Vytvořeno</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.length === 0 ? (
                      <tr><Td colSpan={7} className="text-center py-8 text-gray-500">Žádné rezervace</Td></tr>
                    ) : bookings.map(b => (
                      <tr key={b.id} className="border-b hover:bg-gray-50" data-testid={`admin-booking-${b.id}`}>
                        <Td>{b.id.slice(0, 8)}…</Td>
                        <Td className="font-medium">{b.location_name}</Td>
                        <Td>{b.check_in} → {b.check_out}</Td>
                        <Td>{b.guests}</Td>
                        <Td className="font-semibold">{b.total_price} Kč</Td>
                        <Td><StatusBadge status={b.status} /></Td>
                        <Td className="text-xs text-gray-500">
                          {b.created_at ? new Date(b.created_at).toLocaleDateString('cs-CZ') : '—'}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="locations">
            <Card>
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-left">
                      <Th>Název</Th>
                      <Th>Kategorie</Th>
                      <Th>Cena</Th>
                      <Th>Status</Th>
                      <Th>Aktivní</Th>
                      <Th>Akce</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {locations.map(l => (
                      <tr key={l.id} className="border-b hover:bg-gray-50" data-testid={`admin-loc-${l.id}`}>
                        <Td className="font-medium">{l.name}</Td>
                        <Td>{l.category}</Td>
                        <Td>{l.price} Kč/noc</Td>
                        <Td><StatusBadge status={l.status || 'approved'} /></Td>
                        <Td>
                          {l.is_active === false ? (
                            <Badge className="bg-gray-200 text-gray-700">Pozastaveno</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-700">Aktivní</Badge>
                          )}
                        </Td>
                        <Td>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setPreviewLocation(l)}
                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                              data-testid={`admin-preview-${l.id}`}
                            >
                              <Eye className="w-3 h-3 mr-1" /> Náhled
                            </Button>
                            {l.status !== 'approved' && (
                              <Button size="sm" onClick={() => handleApprove(l.id)} className="bg-green-600 hover:bg-green-700" data-testid={`admin-approve-${l.id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Schválit
                              </Button>
                            )}
                            {l.status !== 'suspended' && (
                              <Button size="sm" variant="outline" onClick={() => handleSuspend(l.id)} className="text-red-600 border-red-300" data-testid={`admin-suspend-${l.id}`}>
                                <XCircle className="w-3 h-3 mr-1" /> Pozastavit
                              </Button>
                            )}
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <UsersPanel users={users} onChange={fetchAll} />
          </TabsContent>

          <TabsContent value="messages">
            <MessagesPanel
              messages={messages}
              onMarkRead={handleMarkRead}
              onDelete={handleDeleteMessage}
            />
          </TabsContent>

          <TabsContent value="interest">
            <Card data-testid="admin-interest-panel">  <CardContent className="p-0">
                <div className="p-4 border-b bg-amber-50">
                  <h3 className="font-bold flex items-center gap-2">
                    <Bell className="w-4 h-4 text-amber-600" />
                    Zájemci o rezervaci ({interest.length})
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Návštěvníci, kteří nechali e-mail u míst se stavem "Brzy dostupné".
                    Po dokončení Stripe onboardingu majitele jim můžete dát vědět.
                  </p>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr className="text-left">
                      <Th>E-mail</Th>
                      <Th>Jméno</Th>
                      <Th>Místo</Th>
                      <Th>Datum</Th>
                      <Th>Akce</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {interest.length === 0 ? (
                      <tr>
                        <Td colSpan={5} className="text-center py-8 text-gray-500">
                          Zatím žádní zájemci
                        </Td>
                      </tr>
                    ) : interest.map((it) => (
                      <tr key={it.id} className="border-b hover:bg-gray-50" data-testid={`admin-interest-${it.id}`}>
                        <Td className="font-medium">
                          <a href={`mailto:${it.email}`} className="text-orange-600 hover:underline">
                            {it.email}
                          </a>
                        </Td>
                        <Td>{it.name || '—'}</Td>
                        <Td>{it.location_name || it.location_id?.slice(0, 8) + '…'}</Td>
                        <Td className="text-xs text-gray-500">
                          {it.created_at ? new Date(it.created_at).toLocaleString('cs-CZ') : '—'}
                        </Td>
                        <Td>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteInterest(it.id)}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            data-testid={`admin-interest-delete-${it.id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Smazat
                          </Button>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="consents">
            <Card data-testid="admin-consents-panel">
              <CardContent className="p-0">
                <div className="p-4 border-b bg-emerald-50">
                  <h3 className="font-bold flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-700" />
                    Souhlasy s obchodními podmínkami ({consents.length})
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Pro každou registraci je zde uložen souhlas s aktuální verzí obchodních podmínek včetně IP a času — důkazní materiál pro případ sporu (GDPR § 7 / Občanský zákoník § 1751).
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr className="text-left">
                        <Th>E-mail</Th>
                        <Th>Jméno</Th>
                        <Th>Datum a čas</Th>
                        <Th>Verze podmínek</Th>
                        <Th>IP adresa</Th>
                        <Th>Prohlížeč</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {consents.length === 0 ? (
                        <tr><Td colSpan={6} className="text-center py-8 text-gray-500">Zatím žádné záznamy</Td></tr>
                      ) : consents.map((c) => (
                        <tr key={c.id} className="border-b hover:bg-gray-50" data-testid={`admin-consent-${c.id}`}>
                          <Td className="font-medium text-emerald-700">{c.user_email}</Td>
                          <Td>{c.user_name || '—'}</Td>
                          <Td className="text-xs">{c.accepted_at ? new Date(c.accepted_at).toLocaleString('cs-CZ') : '—'}</Td>
                          <Td><span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-semibold">{c.terms_version || '—'}</span></Td>
                          <Td className="text-xs font-mono">{c.ip_address || '—'}</Td>
                          <Td className="text-xs text-gray-500 max-w-xs truncate" title={c.user_agent}>{(c.user_agent || '').slice(0, 60)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="resend">
            <ResendEmailsPanel users={users} />
          </TabsContent>

          <TabsContent value="hiw">
            <HowItWorksPanel />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsPanel />
          </TabsContent>

          <TabsContent value="terms">
            <TermsPanel />
          </TabsContent>
        </Tabs>
      </div>

      <LocationPreviewDialog
        open={!!previewLocation}
        onOpenChange={(v) => !v && setPreviewLocation(null)}
        location={previewLocation}
      />

      <Footer />
    </div>
  );
};

const Kpi = ({ label, value, icon, hint, highlight }) => (
  <Card className={highlight ? 'border-2 border-orange-300 shadow-lg' : ''}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-2">
        <p className="text-sm text-gray-600">{label}</p>
        <div className={highlight ? 'text-orange-600' : 'text-gray-400'}>{icon}</div>
      </div>
      <p className={`text-3xl font-bold ${highlight ? 'text-orange-600' : 'text-gray-900'}`}>{value}</p>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </CardContent>
  </Card>
);

const Th = ({ children }) => <th className="px-4 py-3 font-semibold text-gray-700">{children}</th>;
const Td = ({ children, className = '', colSpan }) => (
  <td colSpan={colSpan} className={`px-4 py-3 ${className}`}>{children}</td>
);

const STATUS_LABELS = {
  pending: { label: 'Čekající', cls: 'bg-yellow-100 text-yellow-700' },
  pending_approval: { label: 'Ke schválení', cls: 'bg-orange-100 text-orange-700' },
  approved: { label: 'Schváleno', cls: 'bg-blue-100 text-blue-700' },
  rejected: { label: 'Odmítnuto', cls: 'bg-red-100 text-red-700' },
  confirmed: { label: 'Potvrzeno', cls: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Zrušeno', cls: 'bg-red-100 text-red-700' },
  suspended: { label: 'Pozastaveno', cls: 'bg-gray-200 text-gray-700' },
};
const StatusBadge = ({ status }) => {
  const meta = STATUS_LABELS[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return <Badge className={meta.cls}>{meta.label}</Badge>;
};


const UsersPanel = ({ users, onChange }) => {
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [syncingId, setSyncingId] = useState(null);

  const syncStripe = async (userId) => {
    setSyncingId(userId);
    try {
      const res = await adminAPI.syncUserStripe(userId);
      const d = res.data || {};
      if (d.charges_enabled) {
        toast.success(`Stripe synchronizován: charges_enabled ✓, payouts ${d.payouts_enabled ? '✓' : '✗'}. Lokality jsou nyní bookable.`);
      } else {
        const reason = d.disabled_reason ? ` (důvod: ${d.disabled_reason})` : '';
        const due = (d.currently_due || []).length ? ` Chybí: ${d.currently_due.join(', ')}` : '';
        toast.warning(`Stripe synchronizován, ale charges_enabled = false${reason}.${due}`);
      }
      onChange && onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Chyba';
      toast.error(`Sync selhal: ${detail}`);
    } finally {
      setSyncingId(null);
    }
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setDraft({ name: u.name || '', email: u.email || '', phone: u.phone || '' });
  };
  const cancelEdit = () => { setEditingId(null); setDraft({ name: '', email: '', phone: '' }); };

  const save = async (userId) => {
    setSaving(true);
    try {
      await adminAPI.updateUser(userId, draft);
      toast.success('Profil aktualizován');
      setEditingId(null);
      onChange && onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Chyba';
      toast.error(`Nelze uložit: ${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const filtered = (users || []).filter(u => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Hledat uživatele podle jména nebo e-mailu…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md bg-white"
          data-testid="users-search"
        />
        <span className="text-sm text-gray-600">{filtered.length} z {users.length} uživatelů</span>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr className="text-left">
                <Th>Jméno</Th>
                <Th>E-mail</Th>
                <Th>Telefon</Th>
                <Th>Role</Th>
                <Th>Stripe</Th>
                <Th>Akce</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => {
                const isEd = editingId === u.id;
                return (
                  <tr key={u.id} className="border-b hover:bg-gray-50" data-testid={`admin-user-${u.id}`}>
                    <Td>
                      {isEd ? (
                        <Input value={draft.name} onChange={(e) => setDraft(d => ({ ...d, name: e.target.value }))} className="h-8 text-sm" data-testid={`user-name-${u.id}`} />
                      ) : (
                        <span className="font-medium">{u.name}</span>
                      )}
                    </Td>
                    <Td>
                      {isEd ? (
                        <Input value={draft.email} onChange={(e) => setDraft(d => ({ ...d, email: e.target.value }))} className="h-8 text-sm" type="email" data-testid={`user-email-${u.id}`} />
                      ) : (
                        u.email
                      )}
                    </Td>
                    <Td>
                      {isEd ? (
                        <Input value={draft.phone} onChange={(e) => setDraft(d => ({ ...d, phone: e.target.value }))} className="h-8 text-sm" data-testid={`user-phone-${u.id}`} />
                      ) : (
                        <span className="text-gray-600">{u.phone || '—'}</span>
                      )}
                    </Td>
                    <Td>
                      {u.is_admin ? <Badge className="bg-red-100 text-red-700">Admin</Badge> : <Badge variant="outline">Uživatel</Badge>}
                    </Td>
                    <Td>
                      {u.stripe_account_id ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-green-600 text-xs" title={u.stripe_account_id}>✅ {u.stripe_account_id.slice(0, 10)}…</span>
                          <span className={`text-[10px] ${u.stripe_charges_enabled ? 'text-green-700' : 'text-orange-600'}`}>
                            {u.stripe_charges_enabled ? 'charges_enabled ✓' : 'čeká na ověření'}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </Td>
                    <Td>
                      {isEd ? (
                        <div className="flex gap-1">
                          <Button size="sm" disabled={saving} onClick={() => save(u.id)} className="h-7 bg-green-600 hover:bg-green-700 text-white" data-testid={`user-save-${u.id}`}>
                            {saving ? '…' : 'Uložit'}
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit} className="h-7" data-testid={`user-cancel-${u.id}`}>Zrušit</Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          <Button size="sm" variant="outline" onClick={() => startEdit(u)} className="h-7" data-testid={`user-edit-${u.id}`}>
                            Upravit
                          </Button>
                          {u.stripe_account_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => syncStripe(u.id)}
                              disabled={syncingId === u.id}
                              className="h-7 border-blue-500 text-blue-700 hover:bg-blue-50"
                              data-testid={`user-sync-stripe-${u.id}`}
                              title="Aktualizovat stav Stripe účtu z Stripe API"
                            >
                              {syncingId === u.id ? '…' : 'Sync Stripe'}
                            </Button>
                          )}
                        </div>
                      )}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};


const MessagesPanel = ({ messages, onMarkRead, onDelete }) => {
  if (messages.length === 0) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-gray-500">
          <Mail className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Zatím žádné zprávy z kontaktního formuláře.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-messages-panel">
      {messages.map((m) => (
        <Card
          key={m.id}
          className={m.is_read ? 'opacity-70' : 'border-l-4 border-l-orange-500 shadow-md'}
          data-testid={`message-${m.id}`}
        >
          <CardContent className="p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  {!m.is_read && <Badge className="bg-orange-600 text-white">Nová</Badge>}
                  <h4 className="font-bold text-lg">{m.subject}</h4>
                </div>
                <p className="text-sm text-gray-600">
                  Od: <strong>{m.name}</strong> &lt;
                  <a href={`mailto:${m.email}`} className="text-orange-600 hover:underline">{m.email}</a>
                  &gt;
                </p>
                <p className="text-xs text-gray-400">
                  {new Date(m.created_at).toLocaleString('cs-CZ')}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onMarkRead(m.id, !m.is_read)}
                  data-testid={`mark-${m.is_read ? 'unread' : 'read'}-${m.id}`}
                >
                  {m.is_read ? <Mail className="w-4 h-4" /> : <MailOpen className="w-4 h-4" />}
                  <span className="ml-1">{m.is_read ? 'Nepřečteno' : 'Přečteno'}</span>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => onDelete(m.id)}
                  data-testid={`delete-message-${m.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 whitespace-pre-wrap text-sm text-gray-800">
              {m.message}
            </div>
            <a
              href={`mailto:${m.email}?subject=${encodeURIComponent('Re: ' + m.subject)}&body=${encodeURIComponent(`\n\n--- Původní zpráva ---\nOd: ${m.name}\n\n${m.message}`)}`}
              className="inline-flex items-center gap-1 mt-3 text-sm text-orange-600 hover:underline"
            >
              <Mail className="w-3.5 h-3.5" /> Odpovědět e-mailem
            </a>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const HIW_FIELDS = [
  // Hero (top of homepage)
  { key: 'home_hero_title', label: 'Domů – HERO velký nadpis (pro zalomení použij nový řádek Enter)', type: 'textarea', section: 'home' },
  { key: 'home_hero_subtitle', label: 'Domů – HERO podtitulek (pro zalomení použij nový řádek Enter)', type: 'textarea', section: 'home' },
  // Hero CTA (Add Location button) on home
  { key: 'home_cta_owner_title', label: 'Domů – CTA tlačítko (Jsi majitel pozemku?)', type: 'input', section: 'home' },
  { key: 'home_cta_owner_subtitle', label: 'Domů – CTA podtitulek (Hledáme nové hostitele)', type: 'input', section: 'home' },
  // Homepage section (3 boxes)
  { key: 'home_hiw_title', label: 'Domů – nadpis sekce', type: 'input', section: 'home' },
  { key: 'home_hiw_subtitle', label: 'Domů – podnadpis', type: 'input', section: 'home' },
  { key: 'home_hiw_step1_title', label: 'Domů – Box 1 nadpis', type: 'input', section: 'home' },
  { key: 'home_hiw_step1_text', label: 'Domů – Box 1 text', type: 'textarea', section: 'home' },
  { key: 'home_hiw_step2_title', label: 'Domů – Box 2 nadpis', type: 'input', section: 'home' },
  { key: 'home_hiw_step2_text', label: 'Domů – Box 2 text', type: 'textarea', section: 'home' },
  { key: 'home_hiw_step3_title', label: 'Domů – Box 3 nadpis', type: 'input', section: 'home' },
  { key: 'home_hiw_step3_text', label: 'Domů – Box 3 text', type: 'textarea', section: 'home' },
  // Features section (4 icon cards) on home
  { key: 'home_features_title', label: 'Domů – Features nadpis sekce', type: 'input', section: 'features' },
  { key: 'home_features_subtitle', label: 'Domů – Features podnadpis', type: 'textarea', section: 'features' },
  { key: 'home_feat1_title', label: 'Features – Box 1 nadpis (🌲 ODLEHLÁ PŘÍRODA)', type: 'input', section: 'features' },
  { key: 'home_feat1_text', label: 'Features – Box 1 text', type: 'textarea', section: 'features' },
  { key: 'home_feat2_title', label: 'Features – Box 2 nadpis (🚙 OFFROAD)', type: 'input', section: 'features' },
  { key: 'home_feat2_text', label: 'Features – Box 2 text', type: 'textarea', section: 'features' },
  { key: 'home_feat3_title', label: 'Features – Box 3 nadpis (⛺ STAN)', type: 'input', section: 'features' },
  { key: 'home_feat3_text', label: 'Features – Box 3 text', type: 'textarea', section: 'features' },
  { key: 'home_feat4_title', label: 'Features – Box 4 nadpis (🔥 OHEŇ)', type: 'input', section: 'features' },
  { key: 'home_feat4_text', label: 'Features – Box 4 text', type: 'textarea', section: 'features' },
  // /how-it-works page
  { key: 'hiw_step1_title', label: 'Stránka /how-it-works – Krok 1 nadpis', type: 'input', section: 'page' },
  { key: 'hiw_step1_text', label: 'Stránka /how-it-works – Krok 1 text', type: 'textarea', section: 'page' },
  { key: 'hiw_step2_title', label: 'Stránka /how-it-works – Krok 2 nadpis', type: 'input', section: 'page' },
  { key: 'hiw_step2_text', label: 'Stránka /how-it-works – Krok 2 text', type: 'textarea', section: 'page' },
  { key: 'hiw_step3_title', label: 'Stránka /how-it-works – Krok 3 nadpis', type: 'input', section: 'page' },
  { key: 'hiw_step3_text', label: 'Stránka /how-it-works – Krok 3 text', type: 'textarea', section: 'page' },
  { key: 'hiw_faq_q1', label: 'FAQ 1 – otázka', type: 'input', section: 'faq' },
  { key: 'hiw_faq_a1', label: 'FAQ 1 – odpověď', type: 'textarea', section: 'faq' },
  { key: 'hiw_faq_q2', label: 'FAQ 2 – otázka', type: 'input', section: 'faq' },
  { key: 'hiw_faq_a2', label: 'FAQ 2 – odpověď', type: 'textarea', section: 'faq' },
  { key: 'hiw_faq_q3', label: 'FAQ 3 – otázka', type: 'input', section: 'faq' },
  { key: 'hiw_faq_a3', label: 'FAQ 3 – odpověď', type: 'textarea', section: 'faq' },
  { key: 'hiw_faq_q4', label: 'FAQ 4 – otázka', type: 'input', section: 'faq' },
  { key: 'hiw_faq_a4', label: 'FAQ 4 – odpověď', type: 'textarea', section: 'faq' },
];

const HIW_SECTION_LABELS = {
  home: '🏠 Domovská stránka – sekce "Jak to funguje" (3 boxíky)',
  features: '🎯 Domovská stránka – sekce "Odlehlá místa" (4 boxíky s ikonami)',
  page: '📄 Stránka „Jak to funguje" – kroky',
  faq: '❓ Časté dotazy (FAQ)',
};

const EMAIL_TYPES = [
  { value: 'welcome',                  label: 'Welcome (Vítej v WildCamping)', needsBooking: false },
  { value: 'booking_received',         label: 'Rezervace vytvořena',           needsBooking: true },
  { value: 'booking_approved',         label: 'Rezervace schválena (zaplaťte)',needsBooking: true },
  { value: 'booking_rejected',         label: 'Rezervace odmítnuta',           needsBooking: true },
  { value: 'booking_confirmed',        label: 'Platba potvrzena',              needsBooking: true },
  { value: 'day_before',               label: 'Připomenutí den před',          needsBooking: true },
  { value: 'approval_request_to_host', label: 'Žádost o schválení → hostiteli',needsBooking: true },
  { value: 'review_reminder_guest',    label: 'Ohodnoť pobyt → host (guest)',  needsBooking: true },
  { value: 'review_reminder_host',     label: 'Ohodnoť hosta → hostitel',      needsBooking: true },
];

const ResendEmailsPanel = ({ users }) => {
  const [userId, setUserId] = useState('');
  const [emailType, setEmailType] = useState('welcome');
  const [userBookings, setUserBookings] = useState([]);
  const [bookingId, setBookingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');

  const selectedType = EMAIL_TYPES.find(t => t.value === emailType);
  const needsBooking = !!selectedType?.needsBooking;

  useEffect(() => {
    if (!userId || !needsBooking) { setUserBookings([]); setBookingId(''); return; }
    setLoading(true);
    adminAPI.listUserBookings(userId)
      .then(res => { setUserBookings(res.data || []); setBookingId(''); })
      .catch(() => { setUserBookings([]); setBookingId(''); })
      .finally(() => setLoading(false));
  }, [userId, needsBooking]);

  const filteredUsers = (users || [])
    .filter(u => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    })
    .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));

  const selectedUser = (users || []).find(u => u.id === userId);

  const handleSend = async () => {
    if (!userId) { toast.error('Vyberte uživatele'); return; }
    if (needsBooking && !bookingId) { toast.error('Vyberte rezervaci'); return; }
    setSending(true);
    try {
      await adminAPI.resendEmail({ user_id: userId, email_type: emailType, booking_id: needsBooking ? bookingId : null });
      toast.success(`E-mail "${selectedType.label}" odeslán`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Chyba';
      toast.error(`Nepodařilo se odeslat: ${detail}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="shadow-xl bg-white/95">
      <CardContent className="p-6 space-y-5">
        <div>
          <h3 className="text-xl font-bold mb-1">Doposlat e-mail uživateli</h3>
          <p className="text-sm text-gray-600">
            Nástroj pro situace, kdy uživatel z nějakého důvodu nedostal automatický e-mail.
          </p>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">1. Vyberte uživatele</label>
          <Input
            placeholder="Hledat podle jména nebo e-mailu…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mb-2"
            data-testid="resend-user-search"
          />
          <select
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm bg-white"
            data-testid="resend-user-select"
          >
            <option value="">— vyberte uživatele —</option>
            {filteredUsers.map(u => (
              <option key={u.id} value={u.id}>
                {u.name || '(beze jména)'} — {u.email}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">2. Typ e-mailu</label>
          <select
            value={emailType}
            onChange={(e) => setEmailType(e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm bg-white"
            data-testid="resend-email-type"
          >
            {EMAIL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {needsBooking && (
          <div>
            <label className="block text-sm font-semibold mb-1">
              3. Vyberte rezervaci
              {loading && <span className="text-xs text-gray-500 ml-2">(načítám…)</span>}
            </label>
            <select
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              className="w-full px-3 py-2 border rounded-md text-sm bg-white"
              disabled={!userId || loading}
              data-testid="resend-booking-select"
            >
              <option value="">— vyberte rezervaci —</option>
              {userBookings.map(b => (
                <option key={b.id} value={b.id}>{b.summary || b.id}</option>
              ))}
            </select>
            {userId && !loading && userBookings.length === 0 && (
              <p className="text-xs text-amber-700 mt-1">Tento uživatel zatím nemá žádné rezervace.</p>
            )}
          </div>
        )}

        <div className="pt-2 border-t flex flex-wrap items-center gap-3">
          <Button
            onClick={handleSend}
            disabled={sending || !userId || (needsBooking && !bookingId)}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="resend-send-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            {sending ? 'Odesílám…' : 'Odeslat e-mail'}
          </Button>
          {selectedUser && (
            <span className="text-xs text-gray-500">
              Příjemce: <b>{selectedUser.email}</b>
              {emailType === 'approval_request_to_host' && ' (POZN: posílá se hostiteli rezervace)'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};



const HowItWorksPanel = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getSettings()
      .then((res) => setData(res.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Nelze načíst'));
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = Object.fromEntries(
        HIW_FIELDS.map(({ key }) => [key, data?.[key] ?? ''])
      );
      const res = await adminAPI.updateSettings(payload);
      setData(res.data);
      toast.success('Obsah uložen. Změny jsou ihned viditelné na /how-it-works.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při ukládání');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <Card><CardContent className="p-6 text-gray-500">Načítám…</CardContent></Card>;

  return (
    <Card data-testid="admin-hiw-panel">
      <CardContent className="p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <FileText className="w-5 h-5 text-orange-600" />
          <h3 className="text-xl font-bold">Editor textů – „Jak to funguje"</h3>
        </div>
        <p className="text-sm text-gray-600 -mt-2">
          Texty se zobrazují na <strong>domovské stránce</strong> (3 boxíky) a na stránce <strong>/how-it-works</strong>. Změny se projeví okamžitě.
        </p>

        {['home', 'features', 'page', 'faq'].map((sectionKey) => (
          <div key={sectionKey} className="space-y-4 border-t pt-5">
            <h4 className="font-semibold text-base text-orange-700">
              {HIW_SECTION_LABELS[sectionKey]}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {HIW_FIELDS.filter((f) => f.section === sectionKey).map(({ key, label, type }) => (
                <div key={key} className={type === 'textarea' ? 'md:col-span-2' : ''}>
                  <Label htmlFor={`hiw-${key}`}>{label}</Label>
                  {type === 'textarea' ? (
                    <Textarea
                      id={`hiw-${key}`}
                      rows={3}
                      value={data[key] ?? ''}
                      onChange={(e) => setData({ ...data, [key]: e.target.value })}
                      className="mt-1"
                      data-testid={`hiw-input-${key}`}
                    />
                  ) : (
                    <Input
                      id={`hiw-${key}`}
                      value={data[key] ?? ''}
                      onChange={(e) => setData({ ...data, [key]: e.target.value })}
                      className="mt-1"
                      data-testid={`hiw-input-${key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="flex justify-end pt-4 border-t sticky bottom-0 bg-white py-3">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="hiw-save"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Ukládám…' : 'Uložit změny'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SETTINGS_FIELDS = [
  { key: 'contact_email_primary', label: 'Email (hlavní)', placeholder: 'info@wildcamping.cz' },
  { key: 'contact_email_support', label: 'Email (podpora)', placeholder: 'podpora@wildcamping.cz' },
  { key: 'contact_phone', label: 'Telefon', placeholder: '+420 123 456 789' },
  { key: 'contact_phone_hours', label: 'Telefon – dostupnost', placeholder: 'Po-Pá: 9:00 - 18:00' },
  { key: 'address_company', label: 'Adresa – firma', placeholder: 'WildCamping s.r.o.' },
  { key: 'address_street', label: 'Adresa – ulice', placeholder: 'Přírodní 123' },
  { key: 'address_city', label: 'Adresa – město a PSČ', placeholder: '602 00 Brno' },
  { key: 'hours_weekdays', label: 'Pracovní doba – Po-Pá', placeholder: '9:00 - 18:00' },
  { key: 'hours_saturday', label: 'Pracovní doba – Sobota', placeholder: '10:00 - 15:00' },
  { key: 'hours_sunday', label: 'Pracovní doba – Neděle', placeholder: 'Zavřeno' },
];

const SettingsPanel = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getSettings()
      .then((res) => setData(res.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Nelze načíst nastavení'));
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = Object.fromEntries(
        SETTINGS_FIELDS.map(({ key }) => [key, data?.[key] ?? ''])
      );
      const res = await adminAPI.updateSettings(payload);
      setData(res.data);
      toast.success('Nastavení uloženo. Změny jsou ihned viditelné na stránce Kontakt.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při ukládání');
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return (
      <Card><CardContent className="p-6 text-gray-500">Načítám nastavení…</CardContent></Card>
    );
  }

  return (
    <Card data-testid="admin-settings-panel">
      <CardContent className="p-6 space-y-6">
        <div className="flex items-center gap-3 mb-2">
          <SettingsIcon className="w-5 h-5 text-orange-600" />
          <h3 className="text-xl font-bold">Kontaktní údaje (stránka Kontakt)</h3>
        </div>
        <p className="text-sm text-gray-600 -mt-2">
          Tyto údaje se zobrazují na veřejné stránce <strong>/contact</strong>. Změny se projeví okamžitě bez nového nasazení.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {SETTINGS_FIELDS.map((f) => (
            <div key={f.key}>
              <Label htmlFor={`setting-${f.key}`}>{f.label}</Label>
              <Input
                id={`setting-${f.key}`}
                data-testid={`setting-input-${f.key}`}
                placeholder={f.placeholder}
                value={data[f.key] ?? ''}
                onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                className="mt-1"
              />
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-xs text-gray-500">
            {data.updated_at
              ? `Naposledy upraveno: ${new Date(data.updated_at).toLocaleString('cs-CZ')}`
              : ''}
          </p>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-600 hover:bg-orange-700 text-white"
            data-testid="admin-settings-save"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Ukládám…' : 'Uložit změny'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Terms & Conditions editor ─────────────────────────────────
const TermsPanel = () => {
  const [markdown, setMarkdown] = React.useState('');
  const [original, setOriginal] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [previewHtml, setPreviewHtml] = React.useState('');

  React.useEffect(() => {
    setLoading(true);
    adminAPI.getSettings()
      .then((r) => {
        const md = r.data?.terms_markdown || '';
        setMarkdown(md);
        setOriginal(md);
      })
      .catch(() => toast.error('Nepodařilo se načíst podmínky'))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    // Lazy import to avoid bundling on every page render
    import('../utils/markdown').then(({ renderMarkdown }) => {
      setPreviewHtml(renderMarkdown(markdown));
    });
  }, [markdown]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings({ terms_markdown: markdown });
      setOriginal(markdown);
      toast.success('Obchodní podmínky uloženy. Po deployi se projeví na /terms.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Chyba při ukládání');
    } finally {
      setSaving(false);
    }
  };

  const dirty = markdown !== original;

  if (loading) return <p className="text-gray-500 p-6">Načítám…</p>;

  return (
    <Card data-testid="admin-terms-panel">
      <CardContent className="p-6 space-y-4">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <FileText className="w-5 h-5 text-orange-600" />
            Obchodní podmínky
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Editor používá <b>Markdown</b>. Změny se projeví okamžitě na stránce <b>/terms</b>. Pokud editor necháte prázdný, zobrazí se vestavěné podmínky.
          </p>
          <details className="mt-2 text-xs text-gray-500">
            <summary className="cursor-pointer hover:text-gray-700">📖 Nápověda k Markdownu</summary>
            <pre className="mt-2 bg-gray-50 border rounded p-3 text-xs whitespace-pre-wrap">{`# Hlavní nadpis
## Podnadpis
### Menší nadpis

**tučný text**, *kurzíva*

- odrážka 1
- odrážka 2

1.1 Číslovaný odstavec (zachová se „1.1.")
2.3 Funguje libovolná hloubka

[odkaz na něco](https://example.com)

---  (vodorovná čára)`}</pre>
          </details>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="terms-editor" className="text-sm font-semibold">Markdown editor</Label>
            <Textarea
              id="terms-editor"
              rows={28}
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              placeholder="# Obchodní podmínky&#10;&#10;## 1. Úvodní ustanovení&#10;&#10;1.1 …"
              className="mt-1 font-mono text-sm"
              data-testid="terms-editor-textarea"
            />
            <p className="text-xs text-gray-500 mt-1">
              {markdown.length} znaků · {markdown.split(/\s+/).filter(Boolean).length} slov
            </p>
          </div>
          <div>
            <Label className="text-sm font-semibold">Náhled (jak to uvidí návštěvníci)</Label>
            <div
              className="mt-1 bg-white border rounded p-4 text-sm leading-relaxed overflow-auto"
              style={{ minHeight: '600px', maxHeight: '650px' }}
              data-testid="terms-editor-preview"
            >
              {previewHtml ? (
                <div
                  className="[&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1 [&_ul]:list-disc [&_ul]:pl-5 [&_p]:mb-2 [&_a]:text-orange-600 [&_a]:underline [&_strong]:font-semibold"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              ) : (
                <p className="text-gray-400 italic">Začněte psát vlevo – náhled se zobrazí zde.</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <span className="text-sm text-gray-500">
            {dirty ? '● Neuložené změny' : '✓ Vše uloženo'}
          </span>
          <div className="flex gap-2">
            {dirty && (
              <Button
                variant="outline"
                onClick={() => setMarkdown(original)}
                disabled={saving}
                data-testid="terms-revert-btn"
              >
                Vrátit
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !dirty}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="terms-save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Ukládám…' : 'Uložit'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Admin;
